import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { n8nService } from '../core/services/n8n.service';
import { useAuth } from '../../contexts/AuthContext';
import { queryKeys } from '../../services/query/queryKeys';
import { MemberAvatar } from './atoms/AdminPrimitives';
import { MemberSelect } from './atoms/MemberSelect';
import type { TeamMemberData } from '../../types/n8nResponses';

interface AccountManagerSelectorProps {
  campaignId: string;
  currentManagerId: string | null | undefined;
  teamMembers: TeamMemberData[];
  variant?: 'default' | 'compact';
}

const normalizeManagerId = (value: string | null | undefined): string | null =>
  !value || value === 'null' ? null : value;

export const AccountManagerSelector: React.FC<AccountManagerSelectorProps> = ({
  campaignId,
  currentManagerId,
  teamMembers,
  variant = 'default',
}) => {
  const { currentUser, targetEmail } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const effectiveAdminEmail = targetEmail || currentUser?.email || '';
  const normalized = normalizeManagerId(currentManagerId);
  // Explicit manager only. NO fallback-leader pre-fill: showing a person on an
  // unassigned campaign misleads team admins into thinking it's already
  // assigned (it contradicts the "Unassigned" counter). The value must be
  // empty ("Not assigned") until someone is actually chosen.
  const resolvedManagerId = useMemo(() => {
    if (normalized && teamMembers.some((member) => member.user_id === normalized)) {
      return normalized;
    }
    return null;
  }, [normalized, teamMembers]);

  const [localManagerId, setLocalManagerId] = useState<string | null | undefined>(resolvedManagerId);

  React.useEffect(() => {
    setLocalManagerId(resolvedManagerId);
  }, [resolvedManagerId]);

  const currentManager = useMemo(
    () => teamMembers.find((member) => member.user_id === localManagerId) || null,
    [localManagerId, teamMembers]
  );

  const mutation = useMutation({
    mutationFn: (vars: { accountManagerId: string; accountManagerEmail: string }) =>
      n8nService.updateAccountManager({
        campaign_id: campaignId,
        account_manager_id: vars.accountManagerId,
        account_manager_email: vars.accountManagerEmail,
        admin_email: effectiveAdminEmail,
      }),
    onSuccess: (response, vars) => {
      setError(null);
      // OPTIMISTIC PATCH ONLY — no invalidateQueries here. The upstream DB
      // syncs from ClickUp every ~5 min, so an immediate refetch returns the
      // OLD assignment and overwrites this patch, visibly reverting the
      // select (the "first click reverts, second click works" bug — the
      // second attempt worked only because the sync had caught up with the
      // first write by then). The patch keeps the UI truthful immediately;
      // the normal staleness cycle (2-min staleTime, focus refetch, 5-min
      // poll) confirms against upstream once the sync has propagated.
      const patchLists = (queryKey: readonly unknown[]) => {
        queryClient.setQueryData<any>(queryKey, (old) => {
          if (!old?.campaigns) return old;
          return {
            ...old,
            campaigns: old.campaigns.map((c: any) =>
              c.id === campaignId || c.campaign_id === campaignId
                ? {
                    ...c,
                    account_manager_id: vars.accountManagerId,
                    ...(vars.accountManagerEmail ? { account_manager_email: vars.accountManagerEmail } : {}),
                  }
                : c
            ),
          };
        });
      };
      queryClient.getQueryCache().getAll().forEach((query) => {
        if (query.queryKey[0] === 'campaigns' && query.queryKey[1] === 'all') {
          patchLists(query.queryKey);
        }
      });
      // Patch the campaign detail cache so the sidebar shows the new manager immediately.
      queryClient.setQueryData(
        queryKeys.campaigns.detail(campaignId),
        (old: Record<string, any> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            account_manager_id: vars.accountManagerId,
            account_manager_email: vars.accountManagerEmail,
          };
        },
      );
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to update assignment');
      setLocalManagerId(resolvedManagerId);
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const memberId = event.target.value;
    setError(null);

    const member = teamMembers.find((entry) => entry.user_id === memberId);
    if (!member) return;

    setLocalManagerId(memberId);
    mutation.mutate({
      accountManagerId: member.user_id,
      accountManagerEmail: member.email,
    });
  };

  const hasManager = Boolean(currentManager);
  const hasExplicitManager = Boolean(normalized);
  const managerLabel = currentManager?.name || currentManager?.email || 'Account owner';

  if (variant === 'compact') {
    return (
      <div
        className="min-w-0 self-center"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {/* No in-field label — the board's column header carries it */}
        <MemberSelect
          id={`account-manager-${campaignId}`}
          value={localManagerId ?? ''}
          onChange={(memberId) => {
            const member = teamMembers.find((entry) => entry.user_id === memberId);
            if (!member) return;
            setLocalManagerId(memberId);
            mutation.mutate({
              accountManagerId: member.user_id,
              accountManagerEmail: member.email,
            });
          }}
          members={teamMembers}
          label="Assign campaign owner"
          disabled={mutation.isPending}
          placeholder="Not assigned"
          triggerClassName={`h-8 px-2 ${
            hasExplicitManager
              ? 'border-cyan-300/18 bg-cyan-300/10 text-cyan-50 hover:border-cyan-300/30'
              : 'border-amber-300/22 bg-amber-300/10 text-amber-50 hover:border-amber-300/36'
          }`}
        />
        {mutation.isPending && (
          <div className="mt-1.5 h-3.5 w-3.5 animate-spin rounded-full border border-cyan-300 border-t-transparent" />
        )}
        {error && (
          <p className="mt-1.5 rounded-[10px] border border-red-400/20 bg-red-400/10 px-2 py-1 text-[10px] font-medium text-red-200">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3 rounded-[18px] border border-white/8 bg-black/20 px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#68758F]">Current owner</p>
          <p className={`mt-1 truncate text-[12px] font-semibold ${hasManager ? 'text-white' : 'text-amber-100'}`}>
            {managerLabel}
          </p>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${
          hasExplicitManager
            ? 'border-cyan-300/16 bg-cyan-300/10 text-cyan-100'
            : 'border-amber-300/20 bg-amber-300/10 text-amber-100'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${hasExplicitManager ? 'bg-cyan-300' : 'bg-amber-300'}`} />
          {hasExplicitManager ? 'Assigned' : 'Needs assignment'}
        </div>
      </div>

      <div className="relative">
        <select
          value={localManagerId ?? ''}
          onChange={handleChange}
          disabled={mutation.isPending}
          onClick={(event) => event.stopPropagation()}
          className={`w-full cursor-pointer appearance-none rounded-[18px] border px-4 py-3.5 pr-11 text-[11px] font-black uppercase tracking-[0.16em] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all focus:outline-none focus:ring-2 focus:ring-cyan-300/10 disabled:cursor-not-allowed disabled:opacity-60 ${
            hasExplicitManager
              ? 'border-cyan-300/18 bg-[linear-gradient(180deg,rgba(34,211,238,0.10),rgba(34,211,238,0.04))] text-cyan-100 hover:border-cyan-300/30'
              : 'border-white/10 bg-white/[0.04] text-white/72 hover:border-white/16'
          }`}
        >
          {teamMembers.map((member) => (
            <option
              key={member.user_id}
              value={member.user_id}
              className="bg-[#0F1117] text-white normal-case tracking-normal font-medium"
            >
              {member.name || member.email}
            </option>
          ))}
        </select>

        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          {mutation.isPending ? (
            <div className="h-4 w-4 animate-spin rounded-full border border-cyan-300 border-t-transparent" />
          ) : (
            <Icon icon="bx:chevron-down" className="h-4 w-4 text-white/38" aria-hidden="true" />
          )}
        </div>
      </div>

      {error && (
        <p className="rounded-2xl border border-red-400/20 bg-red-400/10 px-3 py-2 text-[11px] font-medium text-red-200">
          {error}
        </p>
      )}
    </div>
  );
};
